import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { Scope, showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

interface UpdateGhostProfileScopeData {
  ghostProfileId: string
  scope: Scope
}

const updateGhostProfileScope = async ({
  ghostProfileId,
  scope,
}: UpdateGhostProfileScopeData): Promise<void> => {
  const formData = new FormData()
  formData.append('scope', scope)
  await api.patch(`/ghost-profiles/${ghostProfileId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useUpdateGhostProfileScope = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateGhostProfileScopeData, {
    previousProfile: unknown
  }>({
    mutationFn: updateGhostProfileScope,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['profile', variables.ghostProfileId] })

      const previousProfile = queryClient.getQueryData(['profile', variables.ghostProfileId])

      queryClient.setQueryData(['profile', variables.ghostProfileId], (old: any) =>
        old ? { ...old, scope: variables.scope } : old,
      )

      return { previousProfile }
    },
    onError: (error, variables, context) => {
      if (context?.previousProfile !== undefined) {
        queryClient.setQueryData(['profile', variables.ghostProfileId], context.previousProfile)
      }
      showApiErrorToast(t, error)
    },
    onSuccess: () => {
      showSuccessToast(t('toast.success'), t('toast.scopeChanged'))
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['profile', variables.ghostProfileId], exact: true })
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
      await queryClient.invalidateQueries({ queryKey: ['birthdays'] })
    },
  })
}
